import React, { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { LayoutDashboard, PackageSearch, PlusCircle, PackageMinus,
  Settings as SettingsIcon, ScrollText, LogOut, Tag, FileText,
  Download, Menu, X } from "lucide-react";
import { useAuth } from "../lib/auth";
import { Toaster } from "sonner";
import ScanTrigger from "./ScanTrigger";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, perm: "dashboard.view", testId: "nav-dashboard" },
  { to: "/stock", label: "Stock", icon: PackageSearch, perm: "stock.view", testId: "nav-stock" },
  { to: "/add", label: "Add Lot", icon: PlusCircle, perm: "stock.add", testId: "nav-add" },
  { to: "/out", label: "Stock Out", icon: PackageMinus, perm: "stock.out", testId: "nav-out" },
  { to: "/po", label: "Purchase Orders", icon: FileText, perm: "po.view", testId: "nav-po" },
  { to: "/labels", label: "Labels", icon: Tag, perm: "labels.print", testId: "nav-labels" },
  { to: "/activity", label: "Activity", icon: ScrollText, perm: "activity.view", testId: "nav-activity" },
  { to: "/reports", label: "Reports", icon: Download, perm: "activity.view", testId: "nav-reports" },
  { to: "/settings", label: "Settings", icon: SettingsIcon, perm: "__admin_only__", testId: "nav-settings" },
];

export default function Layout({ children }) {
  const { user, logout, can } = useAuth();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const loc = useLocation();

  // Close drawer on route change
  React.useEffect(() => { setDrawerOpen(false); }, [loc.pathname]);

  // Compute which nav items are visible for this user
  const nav = NAV.filter(n => {
    if (n.perm === "__admin_only__") return user?.role === "admin";
    return !n.perm || can(n.perm);
  });

  return (
    <div className="min-h-screen bg-[#FAF7F2]">
      <Toaster richColors position="top-right" />

      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 inset-x-0 h-14 bg-white border-b border-[#E5D8C4] z-30 flex items-center justify-between px-4">
        <button onClick={() => setDrawerOpen(true)} data-testid="mobile-menu-btn"
          className="p-2 -ml-2 text-[#2D2622]">
          <Menu size={22} />
        </button>
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-[#2D2622] text-[#FAF7F2] flex items-center justify-center font-display font-black text-sm">VS</div>
          <div className="font-display font-bold text-sm tracking-tight text-[#2D2622]">Veneer Slate</div>
        </div>
        <ScanTrigger compact />
      </div>

      {/* Sidebar (desktop) + Drawer (mobile) */}
      <aside className={`fixed left-0 top-0 h-screen w-64 border-r border-[#E5D8C4] bg-white flex flex-col z-40 transition-transform duration-300
        ${drawerOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}>
        <div className="p-5 border-b border-[#E5D8C4] flex justify-between items-center">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-11 h-11 bg-[#2D2622] text-[#FAF7F2] flex items-center justify-center font-display font-black text-lg">VS</div>
            <div className="min-w-0">
              <div className="font-display font-bold text-base leading-none tracking-tight text-[#2D2622]">Veneer Slate</div>
              <div className="text-[9px] uppercase tracking-[0.25em] text-[#8B6F47] mt-1 font-semibold">Galaxy Impex</div>
            </div>
          </div>
          <button onClick={() => setDrawerOpen(false)} className="lg:hidden p-1 -mr-1 text-[#2D2622]">
            <X size={18}/>
          </button>
        </div>
        <div className="p-3 border-b border-[#F3ECDF]">
          <ScanTrigger />
        </div>
        <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
          {nav.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.to === "/"} data-testid={n.testId}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 text-sm transition-colors ${
                  isActive ? "bg-[#2D2622] text-[#FAF7F2]" : "text-[#5D5148] hover:text-[#2D2622] hover:bg-[#FAF7F2]"
                }`}>
              <n.icon size={17} strokeWidth={1.6} /> <span className="font-medium">{n.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-[#E5D8C4]">
          <div className="flex items-center gap-3 px-1 mb-3">
            <div className="w-9 h-9 bg-[#F3ECDF] flex items-center justify-center font-mono2 font-bold text-sm text-[#2D2622]">
              {(user?.full_name || user?.email || "U").slice(0,1).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-[#2D2622] truncate">{user?.full_name}</div>
              <div className="text-[10px] text-[#8B6F47] truncate uppercase tracking-[0.2em] font-semibold">{user?.role}</div>
            </div>
          </div>
          <button onClick={logout} data-testid="logout-btn"
            className="w-full flex items-center justify-center gap-2 px-3 py-2 text-[10px] uppercase tracking-[0.2em] border border-[#E5D8C4] hover:bg-[#2D2622] hover:text-[#FAF7F2] hover:border-[#2D2622] transition-colors">
            <LogOut size={13} strokeWidth={1.6} /> Sign out
          </button>
        </div>
      </aside>
      {drawerOpen && <div className="lg:hidden fixed inset-0 bg-black/40 z-30" onClick={() => setDrawerOpen(false)} />}

      <main className="lg:ml-64 min-h-screen pt-14 lg:pt-0">
        <div className="p-4 md:p-6 lg:p-8 max-w-[1600px]">{children}</div>
      </main>
    </div>
  );
}
